import SimpleBar from 'simplebar';
import { onBeforeUnmount, onMounted, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';

import { watchForMissingTransition } from '@sg-core/resources/assets/js/utils';

import { STATES } from './collapse';

function recalculateSimpleBar(element: HTMLElement): void {
  const simplebarEl = element.closest('[data-simplebar]');

  if (!simplebarEl) {
    return;
  }

  const instance = SimpleBar.instances.get(simplebarEl as HTMLElement);

  if (instance) {
    instance.recalculate();
  }
}

export interface UseCollapseOptions {
  minHeight?: MaybeRefOrGetter<string | undefined>;
  observeResize?: boolean;
}

export default function useCollapse(
  contentRef: Ref<HTMLElement | null>,
  isOpen: Ref<boolean>,
  { minHeight, observeResize = true }: UseCollapseOptions = {},
): void {
  const restingHeight = (): string => toValue(minHeight) ?? '';

  const signalController = ref<AbortController | null>(null);
  const animation = ref<number | null>(null);
  const previousHeight = ref<number | null>(null);
  const cancelFallback = ref<(() => void) | null>(null);

  const resizeObserver = new ResizeObserver((entries) => {
    if (!isOpen.value) {
      return;
    }

    requestAnimationFrame(() => {
      const content = contentRef.value;

      if (!content) {
        return;
      }

      const height = (entries[0].target as HTMLElement).scrollHeight;
      if (height !== parseFloat(content.style.height)) {
        content.style.height = `${height}px`;
      }
    });
  });

  function clearFallback(): void {
    cancelFallback.value?.();
    cancelFallback.value = null;
  }

  function cancelPendingFrame(): void {
    if (animation.value) {
      cancelAnimationFrame(animation.value);
      animation.value = null;
    }
  }

  function abortCurrentAnimation(): void {
    clearFallback();

    contentRef.value?.classList.remove(STATES.GROWING);
    contentRef.value?.classList.remove(STATES.SHRINKING);

    cancelPendingFrame();

    if (signalController.value && !signalController.value.signal.aborted) {
      signalController.value.abort();
      signalController.value = null;
    }

    signalController.value = new AbortController();
  }

  function expand(): void {
    const content = contentRef.value;

    if (!content) {
      return;
    }

    content.classList.add(STATES.CALCULATING);

    let height = previousHeight.value;

    if (!height) {
      const parent = content.parentElement as HTMLElement;
      let width = parent.getBoundingClientRect().width;
      const parentStyle = getComputedStyle(parent);

      width -= parseFloat(parentStyle.paddingLeft) + parseFloat(parentStyle.paddingRight);

      content.style.width = `${width}px`;

      height = content.getBoundingClientRect().height;

      content.style.width = '';
    }

    abortCurrentAnimation();

    content.style.height = restingHeight();

    animation.value = requestAnimationFrame(() => {
      let finished = false;

      const finish = (): void => {
        if (finished) {
          return;
        }

        finished = true;

        clearFallback();
        content.classList.remove(STATES.TRANSITIONING);
        content.classList.remove(STATES.GROWING);
        content.classList.add(STATES.EXPANDED);
        content.style.height = 'auto';

        if (observeResize && content.firstElementChild) {
          resizeObserver.observe(content.firstElementChild);
        }

        recalculateSimpleBar(content);
      };

      const handleTransitionEnd = (event: TransitionEvent): void => {
        if (event.target !== content || event.propertyName !== 'height') {
          return;
        }

        finish();
      };

      content.addEventListener('transitionend', handleTransitionEnd, {
        signal: signalController.value!.signal,
      });

      content.classList.remove(STATES.CALCULATING);
      content.classList.add(STATES.TRANSITIONING);
      content.classList.add(STATES.GROWING);

      animation.value = requestAnimationFrame(() => {
        content.style.height = `${height}px`;

        cancelFallback.value = watchForMissingTransition(content, finish, 'height');
      });
    });
  }

  function collapse(): void {
    const content = contentRef.value;

    if (!content) {
      return;
    }

    abortCurrentAnimation();
    resizeObserver.disconnect();

    previousHeight.value = content.scrollHeight;

    content.style.height = `${content.getBoundingClientRect().height}px`;

    animation.value = requestAnimationFrame(() => {
      let finished = false;

      const finish = (): void => {
        if (finished) {
          return;
        }

        finished = true;

        clearFallback();
        content.classList.remove(STATES.TRANSITIONING);
        content.classList.remove(STATES.SHRINKING);

        previousHeight.value = null;

        content.style.height = '';

        recalculateSimpleBar(content);
      };

      const handleTransitionEnd = (event: TransitionEvent): void => {
        if (event.target !== content || event.propertyName !== 'height') {
          return;
        }

        finish();
      };

      content.addEventListener('transitionend', handleTransitionEnd, {
        signal: signalController.value!.signal,
      });

      content.classList.add(STATES.TRANSITIONING);
      content.classList.add(STATES.SHRINKING);
      content.classList.remove(STATES.EXPANDED);

      animation.value = requestAnimationFrame(() => {
        content.style.height = restingHeight();

        cancelFallback.value = watchForMissingTransition(content, finish, 'height');
      });
    });
  }

  onMounted(() => {
    if (isOpen.value) {
      expand();
    }
  });

  watch(isOpen, (newValue) => {
    if (newValue) {
      expand();
    } else {
      collapse();
    }
  });

  onBeforeUnmount(() => {
    clearFallback();
    cancelPendingFrame();

    if (signalController.value) {
      signalController.value.abort();
    }
    resizeObserver.disconnect();
  });
}
